import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Package, ShoppingCart, ArrowLeft, Trash2, Edit, Plus,
  Check, X, Clock, TrendingUp, LogOut, Upload, Image as ImageIcon, Flame
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatPrice(price) {
  return new Intl.NumberFormat("fr-FR").format(price) + " FCFA";
}

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  shipped: "Expédiée",
  delivered: "Livrée",
  cancelled: "Annulée",
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [adminName, setAdminName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "", short_name: "", price: 0, original_price: 0, discount_percentage: 0,
    description: "", long_description: "",
    benefits: [], ingredients: "", image_url: "", category: "supplement", in_stock: true,
  });
  const [promoForm, setPromoForm] = useState({
    enabled: false,
    title: "OFFRE FLASH",
    subtitle: "Jusqu'à -20% sur toute la gamme",
    end_date: "",
  });
  const [savingPromo, setSavingPromo] = useState(false);
  const [promoSaved, setPromoSaved] = useState(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return false;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setAdminName(res.data.name);
      return true;
    } catch {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_name");
      localStorage.removeItem("admin_email");
      navigate("/login");
      return false;
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth().then((ok) => { if (ok) fetchData(); });
  }, [checkAuth]);

  async function fetchData() {
    try {
      const headers = getAuthHeaders();
      const [prodRes, orderRes, statsRes, promoRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/promo`).catch(() => ({ data: null })),
      ]);
      setProducts(prodRes.data);
      setOrders(orderRes.data);
      setStats(statsRes.data);
      if (promoRes.data) {
        let endDateLocal = "";
        if (promoRes.data.end_date) {
          const d = new Date(promoRes.data.end_date);
          if (!isNaN(d.getTime())) {
            const pad = (n) => String(n).padStart(2, "0");
            endDateLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          }
        }
        setPromoForm({
          enabled: promoRes.data.enabled || false,
          title: promoRes.data.title || "OFFRE FLASH",
          subtitle: promoRes.data.subtitle || "",
          end_date: endDateLocal,
        });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      }
      console.error("Error fetching admin data:", err);
    }
  }

  async function handleSavePromo(e) {
    e.preventDefault();
    setSavingPromo(true);
    setPromoSaved(false);
    try {
      const payload = { ...promoForm };
      if (payload.end_date) {
        payload.end_date = new Date(payload.end_date).toISOString();
      }
      await axios.put(`${API}/promo`, payload, { headers: getAuthHeaders() });
      setPromoSaved(true);
      setTimeout(() => setPromoSaved(false), 3000);
    } catch (err) {
      console.error("Promo save error:", err);
      alert("Erreur lors de l'enregistrement de la promo");
    } finally {
      setSavingPromo(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    localStorage.removeItem("admin_email");
    navigate("/login");
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setProductForm({ ...productForm, image_url: imageUrl });
    } catch (err) {
      console.error("Upload error:", err);
      alert("Erreur lors de l'upload de l'image");
    } finally {
      setUploading(false);
    }
  }

  async function handleSaveProduct(e) {
    e.preventDefault();
    const headers = getAuthHeaders();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productForm, { headers });
      } else {
        await axios.post(`${API}/products`, productForm, { headers });
      }
      setShowProductForm(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      console.error("Error saving product:", err);
    }
  }

  async function handleDeleteProduct(id) {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      console.error("Error deleting product:", err);
    }
  }

  async function handleUpdateOrderStatus(orderId, status) {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status }, { headers: getAuthHeaders() });
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      console.error("Error updating order:", err);
    }
  }

  async function handleDeleteOrder(id) {
    if (!window.confirm("Supprimer cette commande ?")) return;
    try {
      await axios.delete(`${API}/orders/${id}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      console.error("Error deleting order:", err);
    }
  }

  function resetForm() {
    setProductForm({
      name: "", short_name: "", price: 0, original_price: 0, discount_percentage: 0,
      description: "", long_description: "",
      benefits: [], ingredients: "", image_url: "", category: "supplement", in_stock: true,
    });
  }

  function openEditProduct(product) {
    setEditingProduct(product);
    setProductForm({
      name: product.name, short_name: product.short_name, price: product.price,
      original_price: product.original_price || 0, discount_percentage: product.discount_percentage || 0,
      description: product.description, long_description: product.long_description || "",
      benefits: product.benefits || [], ingredients: product.ingredients || "",
      image_url: product.image_url, category: product.category, in_stock: product.in_stock,
    });
    setShowProductForm(true);
  }

  return (
    <div className="min-h-screen bg-[#F5F0E8]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-link" className="text-stone-500 hover:text-[#6C3FC5] transition-colors">
              <ArrowLeft size={20} />
            </a>
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_kacinoe-health-shop/artifacts/d94t0l0x_Logo%20Sante%20Kacinoe.png"
                alt="Santé KACINOÉ"
                className="h-14 w-auto"
              />
              <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Administration
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-stone-500 text-sm hidden sm:block">{adminName}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-stone-500 hover:text-red-500 text-sm font-medium transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Package size={18} className="text-[#6C3FC5]" />
                <span className="text-stone-500 text-xs font-medium">Produits</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.total_products}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <ShoppingCart size={18} className="text-[#6C3FC5]" />
                <span className="text-stone-500 text-xs font-medium">Commandes</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.total_orders}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <Clock size={18} className="text-yellow-500" />
                <span className="text-stone-500 text-xs font-medium">En attente</span>
              </div>
              <p className="text-2xl font-bold text-stone-900">{stats.pending_orders}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp size={18} className="text-green-500" />
                <span className="text-stone-500 text-xs font-medium">Revenu total</span>
              </div>
              <p className="text-xl font-bold text-stone-900">{formatPrice(stats.total_revenue)}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            data-testid="admin-tab-products"
            onClick={() => setActiveTab("products")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              activeTab === "products" ? "bg-[#6C3FC5] text-white" : "bg-white text-stone-700 hover:bg-stone-50"
            }`}
          >
            <Package size={16} /> Produits
          </button>
          <button
            data-testid="admin-tab-orders"
            onClick={() => setActiveTab("orders")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              activeTab === "orders" ? "bg-[#6C3FC5] text-white" : "bg-white text-stone-700 hover:bg-stone-50"
            }`}
          >
            <ShoppingCart size={16} /> Commandes
          </button>
          <button
            data-testid="admin-tab-promo"
            onClick={() => setActiveTab("promo")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              activeTab === "promo" ? "bg-[#6C3FC5] text-white" : "bg-white text-stone-700 hover:bg-stone-50"
            }`}
          >
            <Flame size={16} /> Promo Flash
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === "products" && (
          <div data-testid="admin-products-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Gestion des produits
              </h2>
              <button
                data-testid="admin-add-product-btn"
                onClick={() => { setEditingProduct(null); resetForm(); setShowProductForm(true); }}
                className="inline-flex items-center gap-2 bg-[#6C3FC5] hover:bg-[#582FB0] text-white px-4 py-2 rounded-full text-sm font-medium transition-all"
              >
                <Plus size={16} /> Ajouter
              </button>
            </div>

            {/* Product Form Modal */}
            {showProductForm && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowProductForm(false)}>
                <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {editingProduct ? "Modifier le produit" : "Nouveau produit"}
                  </h3>
                  <form onSubmit={handleSaveProduct} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Nom</label>
                        <input data-testid="product-form-name" type="text" required value={productForm.name}
                          onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Abréviation</label>
                        <input data-testid="product-form-short-name" type="text" required value={productForm.short_name}
                          onChange={(e) => setProductForm({...productForm, short_name: e.target.value})}
                          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Prix actuel (FCFA)</label>
                        <input data-testid="product-form-price" type="number" required value={productForm.price}
                          onChange={(e) => setProductForm({...productForm, price: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Catégorie</label>
                        <input type="text" value={productForm.category}
                          onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm"
                        />
                      </div>
                    </div>
                    {/* Discount Section */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <label className="block text-sm font-medium text-amber-800 mb-3">Réduction / Promotion</label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-amber-700 mb-1">Prix original (FCFA)</label>
                          <input data-testid="product-form-original-price" type="number" value={productForm.original_price}
                            onChange={(e) => {
                              const origPrice = parseInt(e.target.value) || 0;
                              const currentPrice = productForm.price;
                              const pct = origPrice > 0 && currentPrice < origPrice
                                ? Math.round(((origPrice - currentPrice) / origPrice) * 100) : 0;
                              setProductForm({...productForm, original_price: origPrice, discount_percentage: pct});
                            }}
                            className="w-full px-3 py-2 rounded-lg border border-amber-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm"
                            placeholder="0 = pas de réduction"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-amber-700 mb-1">Réduction (%)</label>
                          <input data-testid="product-form-discount" type="number" min="0" max="99" value={productForm.discount_percentage}
                            onChange={(e) => {
                              const pct = Math.min(99, Math.max(0, parseInt(e.target.value) || 0));
                              const origPrice = productForm.original_price || productForm.price;
                              const newPrice = pct > 0 ? Math.round(origPrice * (1 - pct / 100)) : origPrice;
                              setProductForm({...productForm, discount_percentage: pct, price: newPrice, original_price: origPrice});
                            }}
                            className="w-full px-3 py-2 rounded-lg border border-amber-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm"
                          />
                        </div>
                      </div>
                      {productForm.discount_percentage > 0 && (
                        <p className="text-xs text-amber-700 mt-2">
                          Ancien prix : {formatPrice(productForm.original_price)} → Nouveau : {formatPrice(productForm.price)} (-{productForm.discount_percentage}%)
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-stone-600 mb-1">Description courte</label>
                      <textarea data-testid="product-form-description" required value={productForm.description}
                        onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm" rows={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-stone-600 mb-1">Description détaillée</label>
                      <textarea value={productForm.long_description}
                        onChange={(e) => setProductForm({...productForm, long_description: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm" rows={4}
                        placeholder="Description complète du produit..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-stone-600 mb-1">Ingrédients (séparés par •)</label>
                      <input type="text" value={productForm.ingredients}
                        onChange={(e) => setProductForm({...productForm, ingredients: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm"
                        placeholder="Ingrédient 1 • Ingrédient 2 • Ingrédient 3"
                      />
                    </div>

                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm text-stone-600 mb-1">Image du produit</label>
                      <div className="flex gap-3 items-start">
                        <div className="flex-1">
                          <input data-testid="product-form-image" type="url" value={productForm.image_url}
                            onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-[#6C3FC5] focus:ring-1 focus:ring-[#6C3FC5] outline-none text-sm"
                            placeholder="URL de l'image ou uploadez un fichier"
                          />
                        </div>
                        <label
                          data-testid="product-form-upload-btn"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-[#6C3FC5] text-[#6C3FC5] text-sm font-medium cursor-pointer hover:bg-[#6C3FC5]/5 transition-all ${uploading ? 'opacity-50 cursor-wait' : ''}`}
                        >
                          <Upload size={16} />
                          {uploading ? "Upload..." : "Fichier"}
                          <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                        </label>
                      </div>
                      {productForm.image_url && (
                        <div className="mt-3 relative w-20 h-20 rounded-lg overflow-hidden bg-stone-100">
                          <img src={productForm.image_url} alt="Aperçu" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <input data-testid="product-form-stock" type="checkbox" checked={productForm.in_stock}
                        onChange={(e) => setProductForm({...productForm, in_stock: e.target.checked})}
                        className="w-4 h-4 rounded border-stone-300 text-[#6C3FC5] focus:ring-[#6C3FC5]"
                      />
                      <label className="text-sm text-stone-600">En stock</label>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button type="button" onClick={() => setShowProductForm(false)}
                        className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors">
                        Annuler
                      </button>
                      <button type="submit" data-testid="product-form-submit"
                        className="inline-flex items-center gap-2 bg-[#6C3FC5] hover:bg-[#582FB0] text-white px-5 py-2 rounded-full text-sm font-medium transition-all">
                        <Check size={16} />
                        {editingProduct ? "Mettre à jour" : "Créer"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Products Table */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100">
                      <th className="text-left py-3 px-4 text-stone-500 font-medium">Produit</th>
                      <th className="text-left py-3 px-4 text-stone-500 font-medium">Prix</th>
                      <th className="text-left py-3 px-4 text-stone-500 font-medium hidden md:table-cell">Catégorie</th>
                      <th className="text-left py-3 px-4 text-stone-500 font-medium">Stock</th>
                      <th className="text-right py-3 px-4 text-stone-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} data-testid={`admin-product-row-${product.id}`} className="border-b border-stone-50 hover:bg-stone-50/50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <img src={product.image_url} alt={product.name} className="w-10 h-10 rounded-lg object-cover bg-stone-100" />
                            <div>
                              <p className="font-medium text-stone-900">{product.name}</p>
                              <p className="text-stone-500 text-xs">{product.short_name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <span className="text-[#6C3FC5] font-medium">{formatPrice(product.price)}</span>
                            {product.discount_percentage > 0 && (
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-stone-400 text-xs line-through">{formatPrice(product.original_price)}</span>
                                <span className="bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">-{product.discount_percentage}%</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell text-stone-600">{product.category}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${product.in_stock ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                            {product.in_stock ? "En stock" : "Rupture"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openEditProduct(product)} className="p-1.5 text-stone-500 hover:text-[#6C3FC5] transition-colors">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => handleDeleteProduct(product.id)} className="p-1.5 text-stone-500 hover:text-red-500 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <div data-testid="admin-orders-panel">
            <h2 className="text-lg font-semibold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Gestion des commandes
            </h2>
            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <ShoppingCart size={48} className="text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Aucune commande pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} data-testid={`admin-order-card-${order.id}`} className="bg-white rounded-2xl p-5 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-medium text-stone-900">{order.customer_name}</p>
                        <p className="text-stone-500 text-xs">{order.customer_phone}</p>
                      </div>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[order.status]}`}>
                        {STATUS_LABELS[order.status]}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="text-sm text-stone-600">
                        <span className="font-medium">{order.product_name}</span>
                        <span className="mx-2">x{order.quantity}</span>
                        <span className="text-[#6C3FC5] font-semibold">{formatPrice(order.total_price)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select data-testid={`order-status-select-${order.id}`} value={order.status}
                          onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                          className="text-xs border border-stone-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#6C3FC5]">
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <button onClick={() => handleDeleteOrder(order.id)} className="p-1.5 text-stone-400 hover:text-red-500 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Promo Tab */}
        {activeTab === "promo" && (
          <div data-testid="admin-promo-panel" className="max-w-2xl">
            <h2 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bannière promotionnelle
            </h2>
            <p className="text-stone-500 text-sm mb-6">
              Affichez un bandeau en haut du site avec un compte à rebours pour créer un sentiment d'urgence.
            </p>

            <form onSubmit={handleSavePromo} className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
              {/* Live preview */}
              <div className="bg-gradient-to-r from-[#6C3FC5] via-[#7B4ED4] to-[#582FB0] text-white rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Flame size={16} className="text-[#FFD166]" />
                  <span className="font-bold text-sm">{promoForm.title || "Titre de la promo"}</span>
                  {promoForm.subtitle && (
                    <span className="text-xs opacity-90">— {promoForm.subtitle}</span>
                  )}
                  {promoForm.end_date && (
                    <span className="text-xs bg-white/15 rounded-md px-2 py-0.5 font-mono">
                      jusqu'au {new Date(promoForm.end_date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={promoForm.enabled}
                  onChange={(e) => setPromoForm({ ...promoForm, enabled: e.target.checked })}
                  data-testid="promo-enabled-toggle"
                  className="w-5 h-5 accent-[#6C3FC5]"
                />
                <span className="text-sm font-medium text-stone-700">
                  Activer la bannière promo sur le site
                </span>
              </label>

              <div>
                <label className="block text-sm text-stone-700 mb-1.5 font-medium">Titre</label>
                <input
                  type="text"
                  required
                  value={promoForm.title}
                  onChange={(e) => setPromoForm({ ...promoForm, title: e.target.value })}
                  placeholder="OFFRE FLASH"
                  data-testid="promo-title-input"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-stone-700 mb-1.5 font-medium">Sous-titre (optionnel)</label>
                <input
                  type="text"
                  value={promoForm.subtitle}
                  onChange={(e) => setPromoForm({ ...promoForm, subtitle: e.target.value })}
                  placeholder="Jusqu'à -20% sur toute la gamme"
                  data-testid="promo-subtitle-input"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm text-stone-700 mb-1.5 font-medium">Date de fin</label>
                <input
                  type="datetime-local"
                  required
                  value={promoForm.end_date}
                  onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value })}
                  data-testid="promo-end-date-input"
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none"
                />
                <p className="text-xs text-stone-500 mt-1">
                  La bannière disparaîtra automatiquement après cette date.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={savingPromo}
                  data-testid="promo-save-btn"
                  className="bg-[#6C3FC5] hover:bg-[#582FB0] disabled:opacity-50 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all"
                >
                  {savingPromo ? "Enregistrement..." : "Enregistrer la promo"}
                </button>
                {promoSaved && (
                  <span className="text-[#10B981] text-sm inline-flex items-center gap-1.5">
                    <Check size={16} /> Promo enregistrée
                  </span>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
